"use client";

import React from "react";
import { Download, FileText, Info } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import SectionHeader from "@/components/common/SectionHeader";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface WeaveData {
  name: Record<Lang, string>;
  meshRange: string;
  aperture: string;
  openArea: string;
  alloys: string;
  standard: string;
}
const WEAVE_DATA: WeaveData[] = [{
  name: {
    en: "Plain Weave (Square Mesh)",
    zh: "平纹编织 (方孔网)",
    es: "Tejido liso (malla cuadrada)",
    de: "Glatte Bindung (Quadratmasche)",
    fr: "Tissage uni (maille carrée)",
    pt: "Ponto liso (malha quadrada)"
  },
  meshRange: "1x1 to 500x500",
  aperture: "25µm – 10,000µm",
  openArea: "25% – 85%",
  alloys: "AISI 304, 304L, 316, 316L",
  standard: "DIN ISO 4783-2"
}, {
  name: {
    en: "Twilled Weave",
    zh: "斜纹编织",
    es: "Tejido asargado",
    de: "Köperbindung",
    fr: "Tissage croisé",
    pt: "Ponto sarjado"
  },
  meshRange: "20x20 to 635x635",
  aperture: "20µm – 1,000µm",
  openArea: "20% – 58%",
  alloys: "AISI 316L, 321, 310S",
  standard: "ASTM E2016-20"
}, {
  name: {
    en: "Plain Dutch Weave (PDW)",
    zh: "平纹密纹编织 (PDW)",
    es: "Tejido holandés liso (PDW)",
    de: "Glatte Tresse (PDW)",
    fr: "Tissu hollandais uni (PDW)",
    pt: "Trama holandesa lisa (PDW)"
  },
  meshRange: "8x85 to 80x400 (high-flow to 165x1200)",
  aperture: "20µm – 310µm",
  openArea: "18% – 42%",
  alloys: "316L, 904L, Hastelloy C-22",
  standard: "ISO 9044 Class A"
}, {
  name: {
    en: "Twilled Dutch Weave (TDW)",
    zh: "斜纹密纹编织 (TDW)",
    es: "Tejido holandés asargado (TDW)",
    de: "Köpertresse (TDW)",
    fr: "Tissu hollandais croisé (TDW)",
    pt: "Trama holandesa sarjada (TDW)"
  },
  meshRange: "20x250 to 400x2800",
  aperture: "1µm – 100µm",
  openArea: "12% – 32%",
  alloys: "316L, Alloy 20, Monel 400",
  standard: "ISO 9044 Class A+"
}, {
  name: {
    en: "Reverse Dutch Weave (RDW)",
    zh: "反向密纹编织 (RDW)",
    es: "Tejido holandés inverso (RDW)",
    de: "Panzertresse (RDW)",
    fr: "Tissu hollandais inversé (RDW)",
    pt: "Trama holandesa reversa (RDW)"
  },
  meshRange: "72x15 to 400x120",
  aperture: "15µm – 250µm",
  openArea: "16% – 38%",
  alloys: "AISI 304, 316, 316L",
  standard: "DIN 1210 High-Tensile"
}, {
  name: {
    en: "Five-Heddle Weave",
    zh: "五综编织",
    es: "Tejido de cinco lizos",
    de: "Fünfschaftgewebe",
    fr: "Tissage cinq lisses",
    pt: "Trama de cinco liços"
  },
  meshRange: "10x10 to 120x120",
  aperture: "60µm – 1,500µm",
  openArea: "30% – 65%",
  alloys: "316L, Inconel 600, Hastelloy",
  standard: "ASTM E2016-20"
}];
interface MatrixCopy {
  sectionEyebrow: string;
  badge: string;
  heading: string;
  lead: string;
  colType: string;
  colMeshRange: string;
  colNominalAperture: string;
  colOpenArea: string;
  colAlloys: string;
  colStandard: string;
  footerNote: string;
  downloadAlloyBtn: string;
}
const copyData: Record<Lang, MatrixCopy> = {
  en: {
    sectionEyebrow: "Specification Matrix",
    badge: "Technical Reference Matrix",
    heading: "Weave specifications and mechanical limits",
    lead: "Compare opening ranges, open area calculations, and standard alloy formulations across common industrial weave geometries.",
    colType: "Weave Type",
    colMeshRange: "Mesh Count Range",
    colNominalAperture: "Nominal Aperture (µm)",
    colOpenArea: "Open Area %",
    colAlloys: "Standard Alloys",
    colStandard: "Standard",
    footerNote: "Custom warp and weft wire diameter combinations, high-tensile spring wire grades, and custom roll slitting widths are available upon engineering review.",
    downloadAlloyBtn: "Download Alloy Spec Sheets"
  },
  zh: {
    sectionEyebrow: "规格对照",
    badge: "技术参数对照表",
    heading: "编制结构规格与机械参数对比",
    lead: "快速核对常用工业丝网编制结构的孔径范围、开孔率计算值及执行标准。",
    colType: "编织类型",
    colMeshRange: "目数范围",
    colNominalAperture: "名义孔径 (µm)",
    colOpenArea: "开孔率 %",
    colAlloys: "常规材质",
    colStandard: "执行标准",
    footerNote: "可根据具体工况提供经纬异径定制、特殊耐温高拉力材质及定制分条切片支持。",
    downloadAlloyBtn: "下载材质技术规格书"
  },
  es: {
    sectionEyebrow: "Matriz de especificaciones",
    badge: "Matriz de referencia técnica",
    heading: "Especificaciones de tejido y límites mecánicos",
    lead: "Compare rangos de apertura, cálculo de área abierta y formulaciones estándar de aleaciones en geometrías industriales de tejido.",
    colType: "Tipo de tejido",
    colMeshRange: "Rango de número de malla",
    colNominalAperture: "Abertura nominal (µm)",
    colOpenArea: "% Área abierta",
    colAlloys: "Aleaciones estándar",
    colStandard: "Norma",
    footerNote: "Combinaciones a medida de diámetros de alambre de urdimbre y trama, grados de alta tensión y cortes longitudinales disponibles bajo revisión técnica.",
    downloadAlloyBtn: "Descargar fichas técnicas de aleaciones"
  },
  de: {
    sectionEyebrow: "Spezifikationsmatrix",
    badge: "Technische Referenzmatrix",
    heading: "Gewebespezifikationen und mechanische Grenzwerte",
    lead: "Vergleichen Sie Maschenweiten, freie Siebflächen und Standard-Legierungen gängiger industrieller Gewebegeometrien.",
    colType: "Webart",
    colMeshRange: "Maschenzahlbereich (Mesh)",
    colNominalAperture: "Nennmaschenweite (µm)",
    colOpenArea: "Freie Siebfläche %",
    colAlloys: "Standardlegierungen",
    colStandard: "Norm",
    footerNote: "Kundenspezifische Kett- und Schussdrahtkombinationen, hochfeste Federdrahtgüten und individuelle Rollenschneidbreiten nach technischer Prüfung lieferbar.",
    downloadAlloyBtn: "Werkstoff-Datenblätter herunterladen"
  },
  fr: {
    sectionEyebrow: "Matrice de spécifications",
    badge: "Matrice de référence technique",
    heading: "Spécifications de tissage et limites mécaniques",
    lead: "Comparez les plages d'ouverture, les calculs de surface ouverte et les nuances d'alliages standards selon les géométries de tissage.",
    colType: "Type de tissage",
    colMeshRange: "Plage de mesh",
    colNominalAperture: "Ouverture nominale (µm)",
    colOpenArea: "% de vide",
    colAlloys: "Alliages standard",
    colStandard: "Norme",
    footerNote: "Combinaisons de fils chaîne/trame sur mesure, nuances haute résistance et découpes en bandes disponibles sur étude technique.",
    downloadAlloyBtn: "Télécharger les fiches techniques alliages"
  },
  pt: {
    sectionEyebrow: "Matriz de especificações",
    badge: "Matriz de referência técnica",
    heading: "Especificações de tecelagem e limites mecânicos",
    lead: "Compare faixas de abertura, cálculos de área aberta e formulações de ligas padrão em geometrias industriais de telas.",
    colType: "Tipo de tecelagem",
    colMeshRange: "Faixa de mesh",
    colNominalAperture: "Abertura nominal (µm)",
    colOpenArea: "% Área aberta",
    colAlloys: "Ligas padrão",
    colStandard: "Norma",
    footerNote: "Combinações personalizadas de diâmetro de arame de urdidura e trama, aços de alta tração e larguras sob medida mediante consulta técnica.",
    downloadAlloyBtn: "Baixar fichas técnicas de ligas"
  }
};
export default function Matrix() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="technicalreferencematrix" data-nav="light" className="bg-muted/50 mf-section border-b border-border">
      <div className="mf-container">
        <SectionHeader id="technicalreferencematrix-header" eyebrow={copy.sectionEyebrow} heading={copy.heading} lead={copy.lead} />

        <Reveal delay={100} className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="relative w-full overflow-x-auto">
            <Table className="w-full text-left">
              <TableHeader className="bg-muted/70 border-b border-border">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="sticky left-0 z-20 bg-muted/95 font-semibold text-foreground py-3.5 px-4 min-w-[200px] border-r border-border/70">
                    {copy.colType}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground py-3.5 px-4 min-w-[150px]">
                    {copy.colMeshRange}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground py-3.5 px-4 min-w-[170px]">
                    {copy.colNominalAperture}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground py-3.5 px-4 min-w-[130px]">
                    {copy.colOpenArea}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground py-3.5 px-4 min-w-[200px]">
                    {copy.colAlloys}
                  </TableHead>
                  <TableHead className="font-semibold text-foreground py-3.5 px-4 min-w-[160px]">
                    {copy.colStandard}
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {WEAVE_DATA.map((row, i) => <TableRow key={row.meshRange + i} data-index={i} className="hover:bg-muted/40 border-b border-border/70 transition-colors">
                    <TableCell className="sticky left-0 z-10 bg-card hover:bg-muted/40 font-medium text-foreground py-4 px-4 border-r border-border/70">
                      <span className="block font-default">
                        {pick(lang, row.name)}
                      </span>
                    </TableCell>

                    <TableCell className="py-4 px-4 font-mono text-[13px] sm:text-sm text-foreground">
                      {row.meshRange}
                    </TableCell>

                    <TableCell className="py-4 px-4 font-mono text-[13px] sm:text-sm text-foreground">
                      {row.aperture}
                    </TableCell>

                    <TableCell className="py-4 px-4 font-mono text-[13px] sm:text-sm text-foreground">
                      {row.openArea}
                    </TableCell>

                    <TableCell className="py-4 px-4 text-[13px] sm:text-sm text-muted-foreground font-sans">
                      {row.alloys}
                    </TableCell>

                    <TableCell className="py-4 px-4">
                      <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 font-mono text-[13px] font-medium text-foreground border border-border">
                        {row.standard}
                      </span>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </Reveal>

        <Reveal delay={200} className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg bg-card/60 border border-border text-sm text-muted-foreground">
          <div className="flex items-start sm:items-center gap-2.5">
            <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-primary">
              <Info className="size-4" aria-hidden="true" />
            </span>
            <p className="leading-relaxed">
              {copy.footerNote}
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
              <Link to="/MaterialsAndSurfaceFinishesPage">
                <FileText className="size-4 mr-1.5" />
                <span>
                  {copy.downloadAlloyBtn}
                </span>
                <Download className="size-3.5 ml-1 opacity-70" />
              </Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>;
}